/**
 * Rutas del editor de material — canónica /studio/material + alias legacy.
 */

import type { EvaluationExam } from '@interactive-room';
import {
  type DeliveryProfile,
  type MaterialIntent,
  intentFromDeliveryProfile,
  parseDeliveryProfile,
  resourceTypeForProfile,
  deliveryFormatForProfile,
} from './deliveryProfile';

export const STUDIO_MATERIAL_CANONICAL_PATH = '/studio/material';

/** Alias retrocompatibles — mismos query params que la ruta canónica. */
export const STUDIO_MATERIAL_LEGACY_PATHS: Record<DeliveryProfile, string> = {
  learning_guide: '/studio/guide',
  interactive_class: '/studio/clase',
  assessment: '/studio/exam',
};

const PATH_TO_PROFILE: Record<string, DeliveryProfile> = {
  '/studio/guide': 'learning_guide',
  '/studio/clase': 'interactive_class',
  '/studio/exam': 'assessment',
};

export function deliveryProfileFromStudioPath(pathname: string): DeliveryProfile | null {
  const normalized = pathname.replace(/\/+$/, '');
  return PATH_TO_PROFILE[normalized] ?? null;
}

export interface StudioMaterialUrlOptions {
  profile: DeliveryProfile;
  subjectId?: string;
  courseId?: string;
  examId?: string;
  /** Si true, usa /studio/guide|clase|exam en lugar de /studio/material. */
  legacyPath?: boolean;
}

export function buildStudioMaterialUrl({
  profile,
  subjectId,
  courseId,
  examId,
  legacyPath = false,
}: StudioMaterialUrlOptions): string {
  const intent = intentFromDeliveryProfile(profile);
  const params = new URLSearchParams();
  params.set('profile', profile);
  params.set('intent', intent);
  if (subjectId) params.set('subject', subjectId);
  if (courseId) params.set('course', courseId);
  if (examId) params.set('exam', examId);

  const base = legacyPath ? STUDIO_MATERIAL_LEGACY_PATHS[profile] : STUDIO_MATERIAL_CANONICAL_PATH;
  return `${base}?${params.toString()}`;
}

export function deliveryProfileFromExam(exam: EvaluationExam): DeliveryProfile {
  if (exam.deliveryProfile) return exam.deliveryProfile;
  if (exam.resourceType === 'guide') return 'learning_guide';
  if (exam.deliveryFormat === 'interactive') return 'interactive_class';
  return 'assessment';
}

export function buildStudioMaterialUrlFromExam(
  exam: EvaluationExam,
  subjectId?: string,
  legacyPath = false,
): string {
  return buildStudioMaterialUrl({
    profile: deliveryProfileFromExam(exam),
    subjectId,
    examId: exam.id,
    legacyPath,
  });
}

export interface ResolvedStudioMaterialEntry {
  profile: DeliveryProfile;
  intent: MaterialIntent;
  resourceType: 'guide' | 'exam';
  deliveryFormat?: 'print' | 'interactive';
}

/**
 * Resuelve perfil desde pathname (alias), query (?profile / ?intent) o exam guardado.
 */
export function resolveStudioMaterialEntry(
  pathname: string,
  searchParams: URLSearchParams,
  exam?: EvaluationExam | null,
): ResolvedStudioMaterialEntry | null {
  const fromQuery = parseDeliveryProfile(
    searchParams.get('intent'),
    searchParams.get('profile'),
  );
  const fromPath = deliveryProfileFromStudioPath(pathname);
  const fromExam = exam ? deliveryProfileFromExam(exam) : null;

  const profile = fromQuery ?? fromPath ?? fromExam;
  if (!profile) return null;

  const intent = intentFromDeliveryProfile(profile);
  return {
    profile,
    intent,
    resourceType: resourceTypeForProfile(profile),
    deliveryFormat: deliveryFormatForProfile(profile),
  };
}
